import React, { useState, useEffect } from 'react';

const LeagueScoreDistribution = ({ 
  data, 
  currentWeekPoints, 
  currentWeekPercentile,
  currentWeekInfo = null, // { leagueWeek, seasonWeek, seasonYear }
  title = "League Points Scored",
  className = ""
}) => {
  const [densityData, setDensityData] = useState([]);
  const [quantiles, setQuantiles] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Simple smooth density estimation matching R's geom_density approach
  const smoothDensity = (values, bandwidth) => {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    const padding = range * 0.1; // Add padding like R does
    const start = min - padding;
    const end = max + padding;
    
    // Create many evaluation points for ultra-smooth curve
    const numPoints = 512; // High resolution like R uses
    const step = (end - start) / (numPoints - 1);
    const points = [];
    
    for (let i = 0; i < numPoints; i++) {
      const x = start + (i * step);
      let density = 0;
      
      // Simple Gaussian kernel density estimation
      for (const value of values) {
        const diff = (x - value) / bandwidth;
        density += Math.exp(-0.5 * diff * diff);
      }
      
      density = density / (values.length * bandwidth * Math.sqrt(2 * Math.PI));
      points.push({ x, y: density });
    }
    
    return points;
  };

  useEffect(() => {
    if (!data || !data.length) return;

    const points = data.map(d => d.totalPF).sort((a, b) => a - b);
    const min = Math.min(...points);
    const max = Math.max(...points);
    
    // Calculate quantiles
    const q0 = points[0];
    const q25 = points[Math.floor(points.length * 0.25)];
    const q50 = points[Math.floor(points.length * 0.50)];
    const q75 = points[Math.floor(points.length * 0.75)];
    const q100 = points[points.length - 1];
    
    setQuantiles({ q0, q25, q50, q75, q100 });
    
    // Calculate bandwidth using simplified Silverman's rule of thumb (like R default)
    const n = points.length;
    const stdDev = Math.sqrt(points.reduce((sum, x) => {
      const mean = points.reduce((a, b) => a + b, 0) / n;
      return sum + Math.pow(x - mean, 2);
    }, 0) / (n - 1));
    
    // R's default bandwidth calculation
    const iqr = q75 - q25;
    const bandwidth = 0.9 * Math.min(stdDev, iqr / 1.34) * Math.pow(n, -1/5);
    
    // Generate smooth density curve
    const smoothPoints = smoothDensity(points, bandwidth);
    
    // Assign quartiles to density points
    const processedDensity = smoothPoints.map(point => {
      let quartile = 1;
      if (point.x >= q25) quartile = 2;
      if (point.x >= q50) quartile = 3;
      if (point.x >= q75) quartile = 4;
      
      return { ...point, quartile };
    });
    
    setDensityData(processedDensity);
    
  }, [data]);

  const getQuartileColor = (quartile) => {
    switch (quartile) {
      case 1: return '#ef4444'; // Red
      case 2: return '#f97316'; // Orange  
      case 3: return '#eab308'; // Yellow
      case 4: return '#22c55e'; // Green
      default: return '#6b7280';
    }
  };

  // Create ultra-smooth SVG path using native SVG curves
  const createSmoothPath = (points, fillToBottom = false) => {
    if (points.length < 2) return '';
    
    const minX = Math.min(...densityData.map(d => d.x));
    const maxX = Math.max(...densityData.map(d => d.x));
    const maxY = Math.max(...densityData.map(d => d.y));
    
    // Responsive chart dimensions
    const chartWidth = isMobile ? 320 : 800;
    const chartHeight = isMobile ? 200 : 320;
    
    // Convert to screen coordinates
    const screenPoints = points.map(point => ({
      x: (isMobile ? 15 : 20) + ((point.x - minX) / (maxX - minX)) * (chartWidth - (isMobile ? 30 : 40)),
      y: chartHeight - (isMobile ? 15 : 20) - ((point.y / maxY) * (chartHeight - (isMobile ? 30 : 40)))
    }));
    
    // Start path
    let path = `M ${screenPoints[0].x} ${screenPoints[0].y}`;
    
    // Use smooth cubic bezier curves for ultra-smooth lines
    for (let i = 1; i < screenPoints.length; i++) {
      const current = screenPoints[i];
      const previous = screenPoints[i - 1];
      
      // Calculate control points for smooth curve
      const cpx1 = previous.x + (current.x - previous.x) * 0.3;
      const cpy1 = previous.y;
      const cpx2 = current.x - (current.x - previous.x) * 0.3;
      const cpy2 = current.y;
      
      path += ` C ${cpx1} ${cpy1}, ${cpx2} ${cpy2}, ${current.x} ${current.y}`;
    }
    
    // Close path for filled areas
    if (fillToBottom) {
      const lastPoint = screenPoints[screenPoints.length - 1];
      const firstPoint = screenPoints[0];
      path += ` L ${lastPoint.x} ${chartHeight - (isMobile ? 15 : 20)} L ${firstPoint.x} ${chartHeight - (isMobile ? 15 : 20)} Z`;
    }
    
    return path;
  };

  if (!data || !data.length || !densityData.length) {
    return (
      <div className={`h-48 md:h-[28rem] flex items-center justify-center text-gray-500 ${className}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3"></div>
          <div className="text-sm md:text-base">Loading distribution...</div>
        </div>
      </div>
    );
  }

  const minX = Math.min(...densityData.map(d => d.x));
  const maxX = Math.max(...densityData.map(d => d.x));
  const maxY = Math.max(...densityData.map(d => d.y));
  
  // Responsive chart dimensions
  const chartWidth = isMobile ? 320 : 800;
  const chartHeight = isMobile ? 200 : 320;
  const totalWidth = isMobile ? 350 : 920;
  const totalHeight = isMobile ? 280 : 500;

  return (
    <div className={`h-48 md:h-[28rem] relative bg-white rounded-xl md:rounded-2xl p-3 md:p-4 shadow-lg md:shadow-2xl border border-gray-100 ${className}`}>
      <svg width="100%" height="100%" viewBox={`0 0 ${totalWidth} ${totalHeight}`} className="overflow-visible">
        {/* Define premium gradients and effects */}
        <defs>
          {/* Quartile-specific gradients */}
          <linearGradient id="redGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f87171" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#dc2626" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="orangeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fb923c" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#ea580c" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="yellowGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#d97706" stopOpacity="0.1" />
          </linearGradient>
          <linearGradient id="greenGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#059669" stopOpacity="0.1" />
          </linearGradient>
          
          {/* Premium shadow effects */}
          <filter id="softShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.1" floodColor="#000"/>
          </filter>
          
          <filter id="subtleGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Chart area */}
        <g transform={`translate(${isMobile ? 15 : 60}, ${isMobile ? 20 : 40})`}>
          {/* Clean white background with subtle border */}
          <rect x="0" y="0" width={chartWidth} height={chartHeight} 
                fill="#ffffff" 
                stroke="#f1f5f9" 
                strokeWidth="1" 
                rx={isMobile ? "8" : "12"} 
                filter="url(#softShadow)" />
          
          {/* Subtle horizontal grid lines - fewer on mobile */}
          {(isMobile ? [0.5] : [0.25, 0.5, 0.75]).map((level, i) => (
            <line
              key={i}
              x1={isMobile ? "15" : "20"}
              y1={chartHeight - (level * chartHeight)}
              x2={chartWidth - (isMobile ? 15 : 20)}
              y2={chartHeight - (level * chartHeight)}
              stroke="#f8fafc"
              strokeWidth="1"
            />
          ))}
          
          {/* Quartile boundary lines */}
          {Object.values(quantiles).map((q, i) => {
            if (!q || q < minX || q > maxX) return null;
            const x = (isMobile ? 15 : 20) + ((q - minX) / (maxX - minX)) * (chartWidth - (isMobile ? 30 : 40));
            const labels = ['0%', '25%', '50%', '75%', '100%'];
            
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={isMobile ? "15" : "20"}
                  x2={x}
                  y2={chartHeight - (isMobile ? 15 : 20)}
                  stroke="#e2e8f0"
                  strokeWidth="1"
                  strokeDasharray="3,6"
                />
                
                {/* Clean percentage labels - responsive font size */}
                <text
                  x={x}
                  y={chartHeight + (isMobile ? 18 : 30)}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={isMobile ? "10" : "13"}
                  fontWeight="600"
                >
                  {labels[i]}
                </text>
              </g>
            );
          })}
          
          {/* Draw quartile fills under the curve - each quartile gets its own color */}
          {[1, 2, 3, 4].map(quartile => {
            const quartilePoints = densityData.filter(d => d.quartile === quartile);
            if (quartilePoints.length === 0) return null;
            
            return (
              <path
                key={`quartile-fill-${quartile}`}
                d={createSmoothPath(quartilePoints, true)}
                fill={getQuartileColor(quartile)}
                fillOpacity="0.6"
                stroke="none"
              />
            );
          })}
          
          {/* Main density curve outline */}
          <path
            d={createSmoothPath(densityData, false)}
            fill="none"
            stroke="#1e293b"
            strokeWidth={isMobile ? "2" : "3"}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#subtleGlow)"
          />
          
          {/* Current week indicator - responsive styling */}
          {currentWeekPoints && currentWeekPoints >= minX && currentWeekPoints <= maxX && (
            <g>
              {(() => {
                const x = (isMobile ? 15 : 20) + ((currentWeekPoints - minX) / (maxX - minX)) * (chartWidth - (isMobile ? 30 : 40));
                
                // Find the y-coordinate where the line intersects the curve
                const closestPoint = densityData.reduce((prev, curr) => 
                  Math.abs(curr.x - currentWeekPoints) < Math.abs(prev.x - currentWeekPoints) ? curr : prev
                );
                const intersectionY = chartHeight - (isMobile ? 15 : 20) - ((closestPoint.y / maxY) * (chartHeight - (isMobile ? 30 : 40)));
                
                return (
                  <>
                    {/* Background glow for prominence */}
                    <line
                      x1={x}
                      y1={isMobile ? "15" : "20"}
                      x2={x}
                      y2={chartHeight - (isMobile ? 15 : 20)}
                      stroke="#a855f7"
                      strokeWidth={isMobile ? "6" : "8"}
                      strokeLinecap="round"
                      opacity="0.3"
                    />
                    
                    {/* Main purple dashed line */}
                    <line
                      x1={x}
                      y1={isMobile ? "15" : "20"}
                      x2={x}
                      y2={chartHeight - (isMobile ? 15 : 20)}
                      stroke="#8b5cf6"
                      strokeWidth={isMobile ? "3" : "4"}
                      strokeDasharray={isMobile ? "8,4" : "12,6"}
                      strokeLinecap="round"
                    />
                    
                    {/* Intersection point with curve */}
                    <circle
                      cx={x}
                      cy={intersectionY}
                      r={isMobile ? "3" : "5"}
                      fill="#8b5cf6"
                      stroke="#ffffff"
                      strokeWidth={isMobile ? "2" : "3"}
                    />
                    
                    {/* Enhanced week info label - mobile responsive */}
                    {currentWeekInfo && (
                      <g>
                        {/* Label background - responsive sizing */}
                        <rect
                          x={x - (isMobile ? 35 : 55)}
                          y={isMobile ? "5" : "10"}
                          width={isMobile ? "70" : "110"}
                          height={isMobile ? "30" : "45"}
                          fill="#8b5cf6"
                          rx={isMobile ? "6" : "8"}
                          stroke="#ffffff"
                          strokeWidth="2"
                          filter="url(#softShadow)"
                        />
                        
                        {/* Week info text - responsive layout */}
                        {isMobile ? (
                          <>
                            <text
                              x={x}
                              y="18"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="bold"
                            >
                              {currentWeekInfo.seasonYear} W{currentWeekInfo.seasonWeek}
                            </text>
                            <text
                              x={x}
                              y="28"
                              textAnchor="middle"
                              fill="white"
                              fontSize="7"
                              fontWeight="500"
                            >
                              {currentWeekPoints.toFixed(1)} pts
                            </text>
                          </>
                        ) : (
                          <>
                            <text
                              x={x}
                              y="25"
                              textAnchor="middle"
                              fill="white"
                              fontSize="9"
                              fontWeight="bold"
                            >
                              {currentWeekInfo.seasonYear} Week {currentWeekInfo.seasonWeek}
                            </text>
                            <text
                              x={x}
                              y="35"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="500"
                            >
                              League Week {currentWeekInfo.leagueWeek}
                            </text>
                            <text
                              x={x}
                              y="45"
                              textAnchor="middle"
                              fill="white"
                              fontSize="8"
                              fontWeight="500"
                            >
                              {currentWeekPoints.toFixed(1)} pts
                            </text>
                          </>
                        )}
                      </g>
                    )}
                  </>
                );
              })()}
            </g>
          )}
          
          {/* Clean X-axis values - responsive */}
          {data && data.length > 0 && (() => {
            const dataMin = Math.min(...data.map(d => d.totalPF));
            const dataMax = Math.max(...data.map(d => d.totalPF));
            const step = Math.ceil((dataMax - dataMin) / (isMobile ? 2 : 4)); // Fewer labels on mobile
            const roundedMin = Math.ceil(dataMin / step) * step;
            const values = [];
            for (let v = roundedMin; v <= dataMax; v += step) {
              if (v >= dataMin && v !== Math.round(currentWeekPoints)) values.push(v);
            }
            
            return values.map((value, i) => {
              const x = (isMobile ? 15 : 20) + ((value - minX) / (maxX - minX)) * (chartWidth - (isMobile ? 30 : 40));
              return (
                <text
                  key={i}
                  x={x}
                  y={chartHeight + (isMobile ? 40 : 60)}
                  textAnchor="middle"
                  fill="#64748b"
                  fontSize={isMobile ? "9" : "11"}
                  fontWeight="500"
                >
                  {Math.round(value)}
                </text>
              );
            });
          })()}
          
          {/* Clean X-axis label - responsive positioning */}
          <text
            x={chartWidth / 2}
            y={chartHeight + (isMobile ? 60 : 90)}
            textAnchor="middle"
            fill="#374151"
            fontSize={isMobile ? "12" : "16"}
            fontWeight="600"
          >
            {isMobile && title.length > 20 ? "Points Scored" : title}
          </text>
        </g>
      </svg>
    </div>
  );
};

export default LeagueScoreDistribution;